import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, App, Button, Card, Col, Descriptions, Form, Input, Row, Select, Space, Typography } from 'antd';
import {
  HomeOutlined,
  MailOutlined,
  MessageOutlined,
  PhoneOutlined,
  ReloadOutlined,
  SendOutlined,
  UserOutlined
} from '@ant-design/icons';
import './Contact.css';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { useNavigate } from 'react-router-dom';
import { supportPublicAPI } from '../services/api';
import BrandLogo from '../components/BrandLogo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { formatDate } from '../utils/dateUtils';

const { Text } = Typography;
const { TextArea } = Input;
const CONTACT_TRACKING_STORAGE_KEY = 'allowtru_contact_latest_ticket_id';

const DEFAULT_CATEGORIES = [
  'General Inquiry',
  'Technical Support',
  'Sales Inquiry',
  'Partnership',
  'Security Report',
  'Feature Request',
  'Other'
];

const DEFAULT_CATEGORY = 'General Inquiry';

const Contact = () => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);
  const [supportConfig, setSupportConfig] = useState({
    enabled: true,
    message_max_length: 4000,
    supported_categories: DEFAULT_CATEGORIES
  });
  const [configError, setConfigError] = useState('');
  const [submitResult, setSubmitResult] = useState(null);
  const [trackingTicketId, setTrackingTicketId] = useState('');
  const [trackingData, setTrackingData] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingError, setTrackingError] = useState('');
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [contactCaptcha, setContactCaptcha] = useState({
    prompt: '',
    token: '',
    expires_at: '',
  });
  const { t } = useTranslation();
  const navigate = useNavigate();
  const ct = useCallback((key, fallback) => (i18n.exists(key) ? t(key) : fallback), [t]);
  const currentLanguage = i18n.resolvedLanguage || i18n.language || 'en';

  const copyToClipboard = useCallback(async (value) => {
    const trimmed = String(value || '').trim();
    if (!trimmed) {
      return false;
    }
    const copyWithSelection = () => {
      const textarea = document.createElement('textarea');
      textarea.value = trimmed;
      textarea.setAttribute('readonly', 'readonly');
      textarea.style.position = 'fixed';
      textarea.style.top = '-1000px';
      textarea.style.left = '-1000px';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const copied = document.execCommand('copy');
      document.body.removeChild(textarea);
      return copied;
    };

    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(trimmed);
        return true;
      } catch (error) {
        return copyWithSelection();
      }
    }
    return copyWithSelection();
  }, []);

  const fetchCaptcha = useCallback(async (silent = false) => {
    try {
      setCaptchaLoading(true);
      const response = await supportPublicAPI.getPublicContactCaptcha();
      const data = response?.data?.data || {};
      setContactCaptcha({
        prompt: data.prompt || '',
        token: data.token || '',
        expires_at: data.expires_at || '',
      });
      form.setFieldsValue({ math_captcha_answer: '' });
      if (!silent) {
        message.success(t('contact.captcha.refresh_success'));
      }
    } catch (error) {
      setContactCaptcha({
        prompt: '',
        token: '',
        expires_at: '',
      });
      if (!silent) {
        message.error(t('contact.captcha.load_failed'));
      }
    } finally {
      setCaptchaLoading(false);
    }
  }, [form, message, t]);

  useEffect(() => {
    let mounted = true;

    const fetchConfig = async () => {
      try {
        const response = await supportPublicAPI.getPublicContactConfig();
        const data = response.data && response.data.data ? response.data.data : {};
        if (!mounted) {
          return;
        }
        setSupportConfig({
          enabled: data.enabled !== false,
          message_max_length: data.message_max_length || 4000,
          supported_categories: Array.isArray(data.supported_categories) && data.supported_categories.length > 0
            ? data.supported_categories
            : DEFAULT_CATEGORIES
        });
        setConfigError('');
      } catch (error) {
        if (!mounted) {
          return;
        }
        setSupportConfig({
          enabled: true,
          message_max_length: 4000,
          supported_categories: DEFAULT_CATEGORIES
        });
        setConfigError(t('contact.support.config_error'));
      } finally {
        if (mounted) {
          setConfigLoading(false);
        }
      }
    };

    fetchConfig();
    fetchCaptcha(true);

    return () => {
      mounted = false;
    };
  }, [fetchCaptcha, t]);

  useEffect(() => {
    form.setFieldsValue({ category: DEFAULT_CATEGORY });
    const cachedTicketId = String(window.localStorage.getItem(CONTACT_TRACKING_STORAGE_KEY) || '').trim();
    if (cachedTicketId) {
      setTrackingTicketId(cachedTicketId);
    }
  }, [form]);

  const handleCopyEmail = useCallback(async (email) => {
    const value = String(email || '').trim();
    if (!value) {
      return;
    }

    try {
      const copied = await copyToClipboard(value);
      if (!copied) {
        throw new Error('copy failed');
      }
      message.success(t('contact.email_contact.copy_success', { email: value }));
    } catch (error) {
      message.error(t('contact.email_contact.copy_failed'));
    }
  }, [copyToClipboard, message, t]);

  const loadTracking = useCallback(async (ticketIdValue, silent = false) => {
    const ticketId = String(ticketIdValue || '').trim();
    if (!ticketId) {
      setTrackingData(null);
      setTrackingError('');
      return;
    }
    try {
      setTrackingLoading(true);
      const response = await supportPublicAPI.getPublicTicketTracking(ticketId);
      const data = response?.data?.data || null;
      setTrackingData(data);
      setTrackingError('');
      setTrackingTicketId(ticketId);
      window.localStorage.setItem(CONTACT_TRACKING_STORAGE_KEY, ticketId);
      if (!silent) {
        message.success(t('contact.tracking.lookup_success'));
      }
    } catch (error) {
      setTrackingData(null);
      setTrackingError(t('contact.tracking.lookup_failed'));
      if (!silent) {
        message.error(t('contact.tracking.lookup_failed'));
      }
    } finally {
      setTrackingLoading(false);
    }
  }, [message, t]);

  useEffect(() => {
    if (!trackingTicketId) {
      return;
    }
    loadTracking(trackingTicketId, true);
  }, [loadTracking, trackingTicketId]);

  const refreshMathCaptcha = useCallback(() => {
    fetchCaptcha();
  }, [fetchCaptcha]);

  const mapSubmitError = useCallback((error) => {
    const errorCode = error?.response?.headers?.['x-error-code'] || '';
    const errorMap = {
      SUPPORT_RATE_LIMITED: t('contact.submit.errors.rate_limited'),
      SUPPORT_CAPTCHA_UNAVAILABLE: t('contact.submit.errors.captcha_unavailable'),
      SUPPORT_CAPTCHA_EXPIRED: t('contact.submit.errors.captcha_expired'),
      SUPPORT_CAPTCHA_INVALID: t('contact.submit.errors.captcha_invalid'),
      SUPPORT_TURNSTILE_UNAVAILABLE: t('contact.submit.errors.turnstile_unavailable'),
      SUPPORT_TURNSTILE_FAILED: t('contact.submit.errors.turnstile_failed'),
      SUPPORT_DISABLED: t('contact.submit.errors.disabled'),
      SUPPORT_VALIDATION_FAILED: t('contact.submit.errors.validation_failed'),
      SUPPORT_SUBMIT_FAILED: t('contact.submit.errors.submit_failed'),
    };
    return errorMap[errorCode] || t('contact.submit.error');
  }, [t]);

  const onFinish = useCallback(async (values) => {
    setLoading(true);
    try {
      const response = await supportPublicAPI.submitPublicContact({
        name: values.name,
        company: values.company,
        phone: values.phone,
        email: values.email,
        language: currentLanguage,
        category: values.category,
        subject: values.subject,
        message: values.message,
        captcha_token: contactCaptcha.token,
        captcha_answer: values.math_captcha_answer,
      });

      const data = response.data && response.data.data ? response.data.data : {};
      setSubmitResult(data);
      form.resetFields();
      form.setFieldsValue({ category: DEFAULT_CATEGORY });
      fetchCaptcha(true);
      if (data?.ticket_id) {
        setTrackingTicketId(data.ticket_id);
        window.localStorage.setItem(CONTACT_TRACKING_STORAGE_KEY, data.ticket_id);
        await loadTracking(data.ticket_id, true);
        const copied = await copyToClipboard(data.ticket_id);
        if (copied) {
          message.success(t('contact.submit.success.copied_ticket_id', { ticketId: data.ticket_id }));
        }
      }
      message.success(t('contact.submit.success.title'));
    } catch (error) {
      fetchCaptcha(true);
      message.error(mapSubmitError(error));
    } finally {
      setLoading(false);
    }
  }, [contactCaptcha.token, copyToClipboard, currentLanguage, fetchCaptcha, form, loadTracking, mapSubmitError, message, t]);

  const categories = supportConfig.supported_categories || DEFAULT_CATEGORIES;
  const getCategoryLabel = useCallback((category) => {
    const key = `contact.categories.${category}`;
    return i18n.exists(key) ? t(key) : category;
  }, [t]);
  const getStatusLabel = useCallback((status) => {
    const statusMap = {
      Open: t('support_tickets.status_values.open'),
      'In Progress': t('support_tickets.status_values.in_progress'),
      Pending: t('support_tickets.status_values.in_progress'),
      Resolved: t('support_tickets.status_values.resolved'),
      Closed: t('support_tickets.status_values.closed'),
    };
    return statusMap[status] || status || '-';
  }, [t]);
  const categoryOptions = useMemo(
    () => categories.map((category) => ({
      label: getCategoryLabel(category),
      value: category,
    })),
    [categories, getCategoryLabel]
  );
  const submitDisabled = loading || configLoading || captchaLoading || !contactCaptcha.token || !supportConfig.enabled;

  return (
    <div className="contact-container contact-background">
      <div className="contact-topbar">
        <LanguageSwitcher />
        <Button
          size="large"
          className="back-home-top-btn"
          icon={<HomeOutlined />}
          onClick={() => navigate('/')}
        >
          {t('common.actions.back_home')}
        </Button>
      </div>
      <div className="contact-header">
        <div className="contact-logo">
          <BrandLogo size="lg" tone="light" />
        </div>
        <h1 className="contact-title">{t('contact.header.title')}</h1>
        <div className="contact-subtitle">
          <div>{t('contact.header.subtitle')}</div>
          <button
            type="button"
            className="contact-header-email"
            onClick={() => handleCopyEmail(t('contact.header.support_email'))}
          >
            {t('contact.header.support_email')}
          </button>
        </div>
      </div>

      <div className="contact-content">
      <Row gutter={[24, 24]} justify="center" align="stretch">
        <Col xs={24} lg={14} xl={13}>
          <Card title={t('contact.card.send_message.title')} className="contact-card contact-form-card">
            {submitResult && (
              <Alert
                className="contact-alert"
                type="success"
                showIcon
                message={t('contact.submit.success.title')}
                description={
                  <span>
                    {t('contact.submit.success.ticket_id')}: <Text code>{submitResult.ticket_id}</Text>
                  </span>
                }
              />
            )}

            {!supportConfig.enabled && (
              <Alert
                className="contact-alert"
                type="warning"
                showIcon
                message={t('contact.support.disabled')}
              />
            )}

            {configError && (
              <Alert
                className="contact-alert"
                type="error"
                showIcon
                message={configError}
              />
            )}

            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              size="large"
              className="contact-form"
            >
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={t('contact.form.name.label')}
                    name="name"
                    rules={[{ required: true, message: t('contact.form.name.required') }]}
                  >
                    <Input prefix={<UserOutlined />} placeholder={t('contact.form.name.required')} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={t('contact.form.email.label')}
                    name="email"
                    rules={[
                      { required: true, message: t('contact.form.email.required') },
                      { type: 'email', message: t('contact.form.email.invalid') }
                    ]}
                  >
                    <Input prefix={<MailOutlined />} placeholder={t('contact.form.email.required')} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={ct('contact.form.phone.label', 'Phone')}
                    name="phone"
                    rules={[{ required: true, message: ct('contact.form.phone.required', 'Please enter your phone number') }]}
                  >
                    <Input prefix={<PhoneOutlined />} placeholder={ct('contact.form.phone.placeholder', 'Enter your phone number')} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={t('contact.form.company.label')}
                    name="company"
                    rules={[{ required: true, message: t('contact.form.company.required') }]}
                  >
                    <Input placeholder={t('contact.form.company.placeholder')} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={t('contact.form.category.label')}
                    name="category"
                    initialValue={DEFAULT_CATEGORY}
                    rules={[{ required: true, message: t('contact.form.category.required') }]}
                  >
                    <Select
                      placeholder={t('contact.form.category.placeholder')}
                      options={categoryOptions}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={t('contact.form.subject.label')}
                    name="subject"
                    rules={[{ required: true, message: t('contact.form.subject.required') }]}
                  >
                    <Input prefix={<MessageOutlined />} placeholder={t('contact.form.subject.required')} />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label={t('contact.form.content.label')}
                name="message"
                rules={[{ required: true, message: t('contact.form.content.required') }]}
              >
                <TextArea
                  rows={5}
                  maxLength={supportConfig.message_max_length}
                  showCount
                  placeholder={t('contact.form.content.placeholder')}
                />
              </Form.Item>

              <Form.Item
                label={t('contact.captcha.label')}
                name="math_captcha_answer"
                rules={[{ required: true, message: t('contact.captcha.required') }]}
              >
                <Input
                  addonBefore={(
                      <button type="button" className="contact-captcha-trigger" onClick={refreshMathCaptcha} disabled={captchaLoading}>
                        {captchaLoading ? t('contact.captcha.loading') : (contactCaptcha.prompt || t('contact.captcha.unavailable'))}
                    </button>
                  )}
                    suffix={<ReloadOutlined onClick={refreshMathCaptcha} className="contact-captcha-refresh" />}
                  placeholder={t('contact.captcha.placeholder')}
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading || configLoading}
                  disabled={submitDisabled}
                  icon={<SendOutlined />}
                  size="large"
                  block
                  className="contact-submit"
                >
                  {t('contact.button.send')}
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
        <Col xs={24} lg={10} xl={7}>
          <Card title={t('contact.card.track_message.title')} className="contact-card contact-tracking-card">
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <Input.Search
                allowClear
                enterButton={t('contact.tracking.lookup')}
                value={trackingTicketId}
                onChange={(event) => setTrackingTicketId(event.target.value)}
                onSearch={(value) => loadTracking(value)}
                placeholder={t('contact.tracking.placeholder')}
                loading={trackingLoading}
              />
              {trackingError ? <Alert type="warning" showIcon message={trackingError} /> : null}
              {trackingData ? (
                <Descriptions column={1} size="small" bordered>
                  <Descriptions.Item label={t('contact.submit.success.ticket_id')}>
                    <Space>
                      <Text code>{trackingData.ticket_id}</Text>
                      <Button
                        size="small"
                        onClick={async () => {
                          const copied = await copyToClipboard(trackingData.ticket_id);
                          if (copied) {
                            message.success(t('contact.submit.success.copied_ticket_id', { ticketId: trackingData.ticket_id }));
                          }
                        }}
                      >
                        {t('contact.tracking.copy')}
                      </Button>
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('contact.tracking.status')}>
                    {getStatusLabel(trackingData.status)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('contact.tracking.subject')}>
                    {trackingData.subject || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('contact.tracking.latest_reply_time', 'Latest Reply Time')}>
                    {trackingData.latest_reply_at ? formatDate(trackingData.latest_reply_at) : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('contact.tracking.latest_reply')}>
                    <div className="contact-tracking-reply">{trackingData.latest_reply || t('contact.tracking.no_reply')}</div>
                  </Descriptions.Item>
                </Descriptions>
              ) : <Text type="secondary">{t('contact.tracking.empty')}</Text>}
            </Space>
          </Card>
        </Col>
      </Row>
      </div>

    </div>
  );
};

export default Contact;
